import { BrowserModule, HammerModule } from '@angular/platform-browser';
import {
  NgModule,
  LOCALE_ID,
  APP_INITIALIZER,
  SecurityContext,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';

import { RoutingModule } from '@modules/routing/routing.module';
import { AppComponent } from '@components/app.component';
import { MaterialModule } from '@modules/material/material.module';
import {
  DateModule,
  CustomDateAdapter,
  MY_DATE_FORMATS,
} from '@modules/date/date.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FileUploadModule } from 'ng2-file-upload';

import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';

import { AdminComponent } from './components/admin/admin.component';
import { AdminAdminComponent } from './components/admin/admin/admin.component';
import { FavoritesComponent } from './components/admin/favorites/favorites.component';
import { DutySettingsComponent } from './components/admin/admin/duty-settings/duty-settings.component';
import { CommissionComponent } from './components/admin/admin/commission/commission.component';
import { LocationSettingsComponent } from './components/admin/admin/location-settings/location-settings.component';
import { SettingsService } from '@services/settings.service';
import { AuthService } from '@services/auth.service';
import { ImportCustomerComponent } from './components/admin/import-customer/import-customer.component';
import { MenuComponent } from './components/admin/menu/menu.component';
import { VendorsComponent } from './components/admin/vendors/vendors.component';
import { UsersComponent } from './components/admin/users/users.component';
import { AppConfigService } from '@services/app-config.service';
import { PatientService } from '@services/patient.service';
import { TokenInterceptorService } from '@services/token-interceptor.service';
import { PayableComponent } from './components/admin/admin/payable/payable.component';
import { PayableProductsComponent } from './components/admin/admin/payable/payable-products/payable-products.component';
import { PayableOrdersComponent } from './components/admin/admin/payable/payable-orders/payable-orders.component';
import { FixOrdersComponent } from './components/admin/admin/fix-orders/fix-orders.component';
import { FixDuplicateProductsComponent } from './components/admin/admin/fix-duplicate-products/fix-duplicate-products.component';
import { DuplicateProductsComponent } from './components/admin/admin/fix-duplicate-products/duplicate-products/duplicate-products.component';
import { DuplicateProductsHistoryComponent } from './components/admin/admin/fix-duplicate-products/duplicate-products-history/duplicate-products-history.component';
import { ReceivableComponent } from './components/admin/admin/receivable/receivable.component';
import { DiscountManagerComponent } from './components/admin/admin/discount-manager/discount-manager.component';
import { TrunkShowManagerComponent } from './components/admin/admin/trunk-show-manager/trunk-show-manager.component';
import { AcknowledgmentTemplateComponent } from './components/admin/admin/acknowledgment-template/acknowledgment-template.component';
import { SuperbillSetupComponent } from './components/admin/admin/superbill-setup/superbill-setup.component';
import { CommunicationsCenterComponent } from './components/admin/admin/communications-center/communications-center.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { ExamsComponent } from './components/admin/exams/exams.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { VendorInvoicesComponent } from './components/admin/vendors/invoices/invoices.component';
import { VendorAnalyticsComponent } from './components/admin/vendors/analytics/vendor-analytics.component';
import { VendorMarginTestComponent } from './components/admin/vendors/margin/vendor-margin-test.component';
import { VendorStockOrderComponent } from './components/admin/vendors/stock-order/stock-order.component';
import { ChangeLogComponent } from './components/admin/change-log/change-log.component';
import { DesignGalleryComponent } from './components/admin/design-gallery/design-gallery.component';
import { MarkdownModule } from 'ngx-markdown';
import { PayableOrderComponent } from './components/admin/admin/payable/payable-order/payable-order.component';
import { HomeComponent } from '@components/home/home.component';
import { V2Module } from './v2/v2.module';
import { ImageModule } from '@components/admin/image/image.module';
import { PrintModule } from '@components/admin/printing/print.module';
import { AdminSharedModule } from '@modules/admin-shared/admin-shared.module';
import { PatientModule } from '@modules/patient/patient.module';
import { BoardModule } from '@modules/board/board.module';
import { InventoryModule } from '@modules/inventory/inventory.module';
import { ReportsModule } from '@modules/reports/reports.module';

const appInitializerFn = (
  appConfig: AppConfigService,
  settingsService: SettingsService,
  auth: AuthService
) => {
  return () => {
    return appConfig
      .loadAppConfig()
      .catch((err) => {
        console.error('App config load failed', err);
        return undefined;
      })
      .then(() => {
        if (!auth.getToken()) {
          return undefined;
        }
        return settingsService
          .loadSettings()
          .toPromise()
          .catch(() => undefined);
      });
  };
};

@NgModule({
  declarations: [
    AppComponent,
    AdminComponent,
    AdminAdminComponent,
    FavoritesComponent,
    DutySettingsComponent,
    CommissionComponent,
    LocationSettingsComponent,
    ImportCustomerComponent,
    MenuComponent,
    VendorsComponent,
    UsersComponent,
    PayableComponent,
    PayableProductsComponent,
    PayableOrdersComponent,
    FixOrdersComponent,
    FixDuplicateProductsComponent,
    DuplicateProductsComponent,
    DuplicateProductsHistoryComponent,
    ReceivableComponent,
    DiscountManagerComponent,
    TrunkShowManagerComponent,
    AcknowledgmentTemplateComponent,
    SuperbillSetupComponent,
    CommunicationsCenterComponent,
    NavbarComponent,
    ExamsComponent,
    VendorInvoicesComponent,
    VendorAnalyticsComponent,
    VendorMarginTestComponent,
    VendorStockOrderComponent,
    ChangeLogComponent,
    DesignGalleryComponent,
    PayableOrderComponent,
    HomeComponent,
  ],
  imports: [
    BrowserModule,
    HammerModule,
    HttpClientModule,
    DateModule,
    MaterialModule,
    RoutingModule,
    BrowserAnimationsModule,
    FileUploadModule,
    NgxMaskDirective,
    NgxMaskPipe,
    MarkdownModule.forRoot({ sanitize: SecurityContext.NONE }),
    V2Module,
    ImageModule,
    PrintModule,
    AdminSharedModule,
    PatientModule,
    BoardModule,
    InventoryModule,
    ReportsModule,
  ],
  providers: [
    provideNgxMask(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true,
    },
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [AppConfigService, SettingsService, AuthService],
    },
    PatientService,
    DateModule,
    MaterialModule,
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, Platform],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
    { provide: LOCALE_ID, useValue: 'en-US' },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
